// =============================================================================
// EmployeeVocsScreen — /hr/vocs/employee/:employee_id
// =============================================================================
// T-313 / T-252 Phase A. Lists VOC assessments for one employee with
// expiry banding (green / amber / red) and a "+ New VOC" entry point.
// Tab-equivalent placement on the HR portal — accessed via "View VOCs"
// button on HrLicenceDetailScreen.
// =============================================================================
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, BadgeCheck, Plus, ShieldCheck, ChevronRight, Clock,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Card, Pill, Spinner, EmptyState } from "@/components/ui";
import { format, parseISO, addYears, differenceInDays } from "date-fns";
import NewVocModal from "./NewVocModal";

interface VocAssessmentRow {
  voc_assessment_id:   string;
  voc_template_id:     string;
  template_code:       string;
  template_version:    string;
  outcome:             string;
  assessed_at:         string;
  assessor_signed_at:  string | null;
  assessee_signed_at:  string | null;
  director_reviewed_at: string | null;
  voc_template: {
    title:          string;
    asset_kind:     string;
    validity_years: number;
  } | null;
  employee_licence_id: string | null;
}

interface EmployeeRow {
  employee_id: string;
  first_name:  string | null;
  last_name:   string | null;
  primary_role_name: string | null;
}

function outcomeTone(outcome: string): "approved" | "pending" | "rejected" | "default" {
  if (outcome === "competent") return "approved";
  if (outcome === "not_yet_competent") return "rejected";
  if (outcome === "further_training") return "pending";
  return "default";
}

function statusPill(row: VocAssessmentRow): { label: string; tone: "approved" | "pending" | "rejected" | "default" } {
  if (row.outcome === "draft") return { label: "draft — in progress", tone: "pending" };
  const signed = row.assessor_signed_at && row.assessee_signed_at;
  if (!signed) return { label: row.outcome.replace(/_/g, " ") + " — awaiting signatures", tone: "pending" };
  if (!row.director_reviewed_at) return { label: row.outcome.replace(/_/g, " ") + " — pending director review", tone: "pending" };
  return { label: row.outcome.replace(/_/g, " "), tone: outcomeTone(row.outcome) };
}

function expiryFor(row: VocAssessmentRow): { date: Date; band: "ok" | "warn" | "danger" | "n/a"; daysLeft: number } {
  if (row.outcome !== "competent" || !row.assessor_signed_at || !row.assessee_signed_at) {
    return { date: new Date(), band: "n/a", daysLeft: 0 };
  }
  const validity = row.voc_template?.validity_years ?? 2;
  const expiry = addYears(parseISO(row.assessed_at), validity);
  const days = differenceInDays(expiry, new Date());
  let band: "ok" | "warn" | "danger" = "ok";
  if (days <= 0) band = "danger";
  else if (days <= 60) band = "warn";
  return { date: expiry, band, daysLeft: days };
}

export default function EmployeeVocsScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const { employee_id } = useParams<{ employee_id: string }>();
  const [showNew, setShowNew] = useState(false);

  const canManage = hasRole(user, "manager");

  const { data: employee, isLoading: loadingEmp } = useQuery({
    queryKey: ["voc_employee", employee_id],
    enabled: !!employee_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("employee_id, first_name, last_name, primary_role_name")
        .eq("employee_id", employee_id!)
        .single();
      if (error) throw error;
      return data as EmployeeRow;
    },
  });

  const { data: rows = [], isLoading: loadingRows } = useQuery({
    queryKey: ["voc_assessments_for_employee", employee_id],
    enabled: !!employee_id,
    queryFn: async () => {
      // T-313 / mig 161: tables moved to public schema (Supabase REST only
      // exposes public). No .schema("app") prefix needed for these reads.
      const { data, error } = await supabase
        .from("voc_assessments")
        .select(`
          voc_assessment_id, voc_template_id, template_code, template_version,
          outcome, assessed_at, assessor_signed_at, assessee_signed_at,
          director_reviewed_at, employee_licence_id,
          voc_template:voc_template_id(title, asset_kind, validity_years)
        `)
        .eq("employee_id", employee_id!)
        .order("assessed_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as VocAssessmentRow[];
    },
  });

  if (!user) return null;

  const empName = employee
    ? [employee.first_name, employee.last_name].filter(Boolean).join(" ")
    : "—";

  return (
    <div className="p-6 max-w-5xl mx-auto pb-16">
      <Link
        to={`/hr/licences/${employee_id ?? ""}`}
        className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3"
      >
        <ArrowLeft size={14} /> Back to licences
      </Link>

      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div className="min-w-0">
          <div className="text-text-muted text-[11px] uppercase tracking-wider2 font-mono mb-1">HR portal</div>
          <h1 className="text-[24px] font-semibold tracking-tight flex items-center gap-2">
            <BadgeCheck size={22} className="text-accent" />
            VOCs — {empName}
          </h1>
          {employee?.primary_role_name && (
            <div className="text-text-muted text-[13px] mt-1 font-mono">{employee.primary_role_name}</div>
          )}
          <p className="text-text-muted text-[13px] mt-2 max-w-prose">
            Verifications of Competency completed for this employee. Each competent + signed assessment
            auto-creates an entry in the compliance calendar with 60/30/14/7-day renewal alerts.
          </p>
        </div>
        {canManage && (
          <Button icon={Plus} onClick={() => setShowNew(true)}>
            New VOC
          </Button>
        )}
      </div>

      {loadingEmp || loadingRows ? (
        <div className="py-10 flex justify-center"><Spinner size={20} /></div>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            title="No VOCs yet"
            description={
              canManage
                ? "Start one via \"+ New VOC\" — pick a template (Service Locator, Capstan, EWP, Hiab, 4WD) and step through the assessment."
                : "No verifications of competency on record yet."
            }
            icon={ShieldCheck}
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map(row => {
            const pill = statusPill(row);
            const exp = expiryFor(row);
            const expColour =
              exp.band === "danger" ? "text-danger" :
              exp.band === "warn"   ? "text-warn"   :
              exp.band === "ok"     ? "text-ok"     : "text-text-dim";
            return (
              <Card
                key={row.voc_assessment_id}
                className="hover:border-line-light transition-colors cursor-pointer"
              >
                <Link
                  to={`/hr/vocs/${row.voc_assessment_id}`}
                  className="block p-4"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-text font-medium">
                          {row.voc_template?.title ?? row.template_code}
                        </div>
                        <span className="text-[10px] font-mono uppercase tracking-wider2 text-text-dim">
                          {row.template_code} · v{row.template_version}
                        </span>
                      </div>
                      <div className="text-[12px] text-text-muted mt-1 flex items-center gap-3 flex-wrap font-mono">
                        <span><Clock size={11} className="inline mr-1 align-[-2px]" />{format(parseISO(row.assessed_at), "dd MMM yyyy")}</span>
                        {exp.band !== "n/a" && (
                          <span className={expColour}>
                            expires {format(exp.date, "dd MMM yyyy")}
                            {exp.daysLeft > 0 ? ` · ${exp.daysLeft}d left` : ` · ${Math.abs(exp.daysLeft)}d overdue`}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Pill label={pill.label} tone={pill.tone} small />
                      <ChevronRight size={14} className="text-text-muted" />
                    </div>
                  </div>
                </Link>
              </Card>
            );
          })}
        </div>
      )}

      {showNew && employee_id && (
        <NewVocModal
          defaultEmployeeId={employee_id}
          onClose={() => setShowNew(false)}
          onCreated={(newId) => {
            setShowNew(false);
            navigate(`/hr/vocs/${newId}`);
          }}
        />
      )}
    </div>
  );
}
